import React, { forwardRef, useMemo, useState, useEffect, useRef } from "react";
import {
  Flex,
  VisuallyHidden,
  FormControl,
  FormLabel,
  FormLabelProps,
  FormHelperText,
} from "@chakra-ui/react";
import { DateTime } from "luxon";
import { InputSelect } from "./InputSelect";
import { useTranslation } from "react-i18next";

type DateInputProps = {
  name: string;
  value?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  dataTestId?: string;
  label: string | React.ReactNode;
  labelProps?: FormLabelProps;
  isRequired?: boolean;
  hideRequiredAsterisk?: boolean;
  helperText?: string | React.ReactNode;
  autocomplete?: string;
};

// 120 years back from current year
const generateYearOptions = () => {
  const currentYear = DateTime.now().year;
  const years = [];
  for (let i = currentYear; i >= currentYear - 120; i--) {
    years.push({ label: i.toString(), value: i.toString() });
  }
  return years;
};

// Month keys for translation
const monthKeys = [
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
];

// Helper to get days in a month
const getDaysInMonth = (month: number, year: number): number => {
  if (!month || !year) return 31;
  return DateTime.local(year, month).daysInMonth || 31;
};

// Parse YYYY-MM-DD into parts
const parseDate = (dateString?: string): { day: string; month: string; year: string } => {
  if (!dateString) return { day: "", month: "", year: "" };

  const dt = DateTime.fromISO(dateString);
  if (!dt.isValid) return { day: "", month: "", year: "" };

  return {
    year: dt.year.toString(),
    month: dt.month.toString(),
    day: dt.day.toString(),
  };
};

export const DateInput = forwardRef<HTMLInputElement, DateInputProps>((props, ref) => {
  const { t } = useTranslation("common");
  const {
    name,
    value,
    disabled,
    onChange,
    onBlur,
    dataTestId,
    label,
    labelProps,
    isRequired,
    hideRequiredAsterisk,
    helperText,
    autocomplete,
  } = props;

  // Track internal state for each field independently
  const [month, setMonth] = useState("");
  const [day, setDay] = useState("");
  const [year, setYear] = useState("");

  // Track if user has interacted with any field for blur handling
  const hasBlurredRef = useRef(false);

  // Initialize from value prop
  useEffect(() => {
    const parsed = parseDate(value);
    setMonth(parsed.month);
    setDay(parsed.day);
    setYear(parsed.year);
  }, [value]);

  const yearOptions = useMemo(() => generateYearOptions(), []);

  // Generate month options with translated labels
  const monthOptions = useMemo(
    () =>
      monthKeys.map((key, index) => ({
        label: t(`forms.months.${key}`),
        value: (index + 1).toString(),
      })),
    [t],
  );

  // Generate day options based on selected month and year
  const dayOptions = useMemo(() => {
    const monthNum = month ? parseInt(month, 10) : 1;
    const yearNum = year ? parseInt(year, 10) : DateTime.now().year;
    const daysInMonth = getDaysInMonth(monthNum, yearNum);

    const days = [];
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ label: i.toString(), value: i.toString() });
    }
    return days;
  }, [month, year]);

  // Get locale-aware field order (e.g., "month,day,year" for en, "day,month,year" for de)
  const fieldOrder = useMemo(() => {
    const order = t("forms.dateFieldOrder", { defaultValue: "month,day,year" });
    return order.split(",").map((field) => field.trim()) as Array<"month" | "day" | "year">;
  }, [t]);

  const emitDateIfComplete = (newDay: string, newMonth: string, newYear: string) => {
    // Only emit a date string if all three parts are filled
    if (newDay && newMonth && newYear) {
      const monthNum = parseInt(newMonth, 10);
      const dayNum = parseInt(newDay, 10);
      const yearNum = parseInt(newYear, 10);

      // Try to create a DateTime object to validate the date
      let dt = DateTime.local(yearNum, monthNum, dayNum);

      // Check if the date is valid
      if (!dt.isValid) {
        // If the day is invalid, adjust it to the max valid day for that month
        const maxDays = getDaysInMonth(monthNum, yearNum);
        const adjustedDay = Math.min(dayNum, maxDays);
        setDay(adjustedDay.toString());
        dt = DateTime.local(yearNum, monthNum, adjustedDay);
      }

      // Format as YYYY-MM-DD
      const dateString = dt.toISODate();
      if (dateString) {
        onChange?.(dateString);
      }
    }
    // Don't emit anything if incomplete - let the user fill in the fields
  };

  const handleMonthChange = (newMonth: string) => {
    setMonth(newMonth);
    emitDateIfComplete(day, newMonth, year);
  };

  const handleDayChange = (newDay: string) => {
    setDay(newDay);
    emitDateIfComplete(newDay, month, year);
  };

  const handleYearChange = (newYear: string) => {
    setYear(newYear);
    emitDateIfComplete(day, month, newYear);
  };

  // Field configurations for dynamic rendering based on locale
  const fieldConfigs = {
    month: {
      label: t("forms.month"),
      value: month,
      placeholder: t("forms.month"),
      options: monthOptions,
      onChange: handleMonthChange,
      autocompleteSuffix: "month",
    },
    day: {
      label: t("forms.day"),
      value: day,
      placeholder: t("forms.day"),
      options: dayOptions,
      onChange: handleDayChange,
      autocompleteSuffix: "day",
    },
    year: {
      label: t("forms.year"),
      value: year,
      placeholder: t("forms.year"),
      options: yearOptions,
      onChange: handleYearChange,
      autocompleteSuffix: "year",
    },
  };

  // Handle blur events - call onBlur when focus leaves the entire component
  const handleBlur = () => {
    if (!hasBlurredRef.current) {
      hasBlurredRef.current = true;
      // Delay to allow checking if focus moved to another select within the component
      setTimeout(() => {
        onBlur?.();
      }, 0);
    }
  };

  return (
    <FormControl as="fieldset" isRequired={isRequired && !hideRequiredAsterisk}>
      <FormLabel as="legend" fontSize="lg" {...labelProps}>
        {label}
      </FormLabel>

      {/* Hidden input to satisfy ref requirement from react-hook-form */}
      <VisuallyHidden>
        <input
          ref={ref}
          name={name}
          value={value || ""}
          tabIndex={-1}
          readOnly
          aria-hidden="true"
        />
      </VisuallyHidden>

      <Flex gap={3} flexWrap="wrap" alignItems="flex-end">
        {fieldOrder.map((fieldType) => {
          const config = fieldConfigs[fieldType];
          return (
            <FormControl key={fieldType} width="auto">
              <FormLabel htmlFor={`${name}-${fieldType}`} fontSize="md" mb={1}>
                {config.label}
              </FormLabel>
              <InputSelect
                name={`${name}-${fieldType}`}
                value={config.value}
                placeholder={config.placeholder}
                options={config.options}
                onChange={config.onChange}
                onBlur={handleBlur}
                disabled={disabled}
                autocomplete={
                  autocomplete ? `${autocomplete}-${config.autocompleteSuffix}` : undefined
                }
                dataTestPrefix={dataTestId ? `${dataTestId}-${fieldType}` : `${name}-${fieldType}`}
              />
            </FormControl>
          );
        })}
      </Flex>

      {helperText && (
        <FormHelperText fontSize="md" color="gray.800">
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
});
